import { FC, useEffect, useState } from 'react'
import { Route, Routes, useLocation, Navigate } from 'react-router-dom'
import { AppBar, Toolbar } from '@mui/material'
import { useAuth } from '@frontegg/react'

/* Utils */
import theme, { attributes } from '../theme'
import { UserInfo } from '../models'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { closeToast, ToastModel, toastPropsSelector } from '../store/toasts/toastSlice'
import { nexaAIEnabledSelector, nexaAIActivatingSelector } from '../store/user/userSlice'
import { Permission, Role } from '../lib/utils'

/* Components */
import Sidebar from './components/nav/Sidebar'
import Titlebar from './components/nav/Titlebar'
import Settings from './pages/settings'
import Integrations from './pages/settings/integrations/Integrations'
import SetupIntegrationInstance from './pages/settings/integrations/SetupIntegrationInstance'
import UserManagement from './pages/settings/user-management/UserManagement'
import AuditLog from './pages/settings/audit-log/AuditLog'
import Dashboard from './pages/dashboard'
import Library from './pages/library'
import SSM from './pages/security-stack-map/SSM'
import MainLayoutToast from './components/toast/MainLayoutToast'
import MainLayoutToastTrends from './components/toast/MainLayoutToastTrends'
import MainLayoutToastNexaAI from './components/toast/MainLayoutToastNexaAI'
import ProtectedRoute from './ProtectedRoute'
import AccountManagement from './pages/settings/account-management/AccountManagement'
import Profile from './pages/settings/profile/Profile'
import CoverageAnalyzer from './pages/coverage-analyzer'
import AssetInventory from './pages/asset-inventory'
import ZendeskSso from './pages/zendesk/ZendeskSSO'
import FrameworkConfiguration from './pages/framework-configuration/FrameworkConfiguration'
import Framework from './pages/framework-configuration/Framework'
import CategoryPage from './pages/category-dashboard'
import CPIDetails from './pages/cpi-details'
import Reports from './pages/reports'
import ReportsV2 from './pages/reports-v2'
import CustomizeReport from './pages/reports/customize-report'
import MonthlyReportBuilder from './pages/reports/monthly-report-builder'
import QuarterlyReportBuilder from './pages/reports/quarterly-report-builder'
import FullReportBuilder from './pages/reports/full-report-builder'
import OnyxAIInsights from './pages/onyxai-insights'
import NexaAI from './pages/nexa-ai'
import ChooseCustomFramework from './pages/framework-configuration/sections/ChooseCustomFramework'
import SetupJiraInstance from './pages/settings/integrations/jira-integration/SetupJiraInstance'
import SetupApplicationInstance from './pages/settings/integrations/applications/SetupApplicationInstance'
import ApplicationCallback from './pages/settings/integrations/applications/ApplicationCallback'
import ApplicationsCatalog from './pages/settings/integrations/applications/catalog/ApplicationsCatalog'
import LegacyIntegrationsRedirect from './pages/settings/integrations/LegacyIntegrationsRedirect'
import {
  APPLICATION_CALLBACK_PATH,
  DATA_SOURCES_PATH,
  isIntegrationSetupPath,
  isIntegrationsListPath,
  isIntegrationsPath,
  isJiraSetupPath,
  LEGACY_INTEGRATIONS_PATH,
  TICKETING_SYSTEMS_PATH
} from './pages/settings/integrations/integrationPaths'
import { MainLayoutContainer, MainLayoutWrapper } from './mainLayout.styles'

interface Props {
  user: UserInfo
  /* Changes once per account switch, after the old account has been cleared. Reloads the page below
   * so it fetches again for the new account instead of keeping the previous one's data. */
  accountEpoch: number
}

export const MAIN_MENU_WIDTH = 72

const isAssetManagementEnabled = process.env.REACT_APP_ENABLE_ASSET_MANAGEMENT === 'true'

const MainLayout: FC<Props> = ({ user, accountEpoch }) => {
  const location = useLocation()
  const { user: authUser, isAuthenticated } = useAuth()
  const dispatch = useAppDispatch()
  const toast = useAppSelector(toastPropsSelector)
  const nexaAIEnabled = useAppSelector(nexaAIEnabledSelector)
  const nexaAIActivating = useAppSelector(nexaAIActivatingSelector)
  const [showLayout, setShowLayout] = useState(false)

  useEffect(() => {
    setShowLayout(!location.pathname.includes('/create-password'))
  }, [location.pathname])

  const showAppBar =
    location.pathname !== '/dashboard' &&
    !location.pathname.includes('/framework-configuration') &&
    !location.pathname.includes('/library') &&
    location.pathname !== '/settings/user-management' &&
    location.pathname !== '/settings/profile' &&
    location.pathname !== '/settings/audit-log' &&
    location.pathname !== '/settings/account-management' &&
    location.pathname !== '/framework-configuration' &&
    !isIntegrationsPath(location.pathname) &&
    !location.pathname.includes('/security-stack-map') &&
    location.pathname !== '/asset-coverage-analyzer' &&
    !location.pathname.includes('/asset-inventory') &&
    !location.pathname.includes('/insights') &&
    location.pathname !== '/nexa-ai' &&
    !location.pathname.includes('/reports')

  if (!showLayout || !isAuthenticated || !user) return null
  return (
    <MainLayoutContainer
      sx={{
        height: location.pathname.includes('security-stack-map') ? '100vh' : 'unset'
      }}
    >
      {toast.props && toast.open && (
        <>
          {toast.toastModel === ToastModel.NexaAI ? (
            <MainLayoutToastNexaAI
              text={toast.props.text}
              description={toast.props.description}
              handleClose={() => {
                dispatch(closeToast())
              }}
            />
          ) : toast.toastModel === ToastModel.Trends ? (
            <MainLayoutToastTrends
              variant={toast.variant}
              text={toast.props.text}
              description={toast.props.description}
              handleClose={() => {
                dispatch(closeToast())
              }}
            />
          ) : (
            <MainLayoutToast
              variant={toast.variant}
              text={toast.props.text}
              description={toast.props.description}
              handleClose={() => {
                dispatch(closeToast())
              }}
            />
          )}
        </>
      )}

      {showAppBar && (
        <AppBar
          position="fixed"
          sx={{
            width: `calc(100% - ${MAIN_MENU_WIDTH}px)`,
            ml: `${MAIN_MENU_WIDTH}px`,
            boxShadow: attributes.boxShadow.toolbar,
            backgroundColor: theme.colors.primaryDark
          }}
        >
          <Titlebar />
        </AppBar>
      )}

      <Sidebar />

      <MainLayoutWrapper
        component="main"
        className="main-layout"
        isSettingsPage={
          location.pathname.includes('/settings') || location.pathname.includes('/framework-configuration')
            ? 'true'
            : 'false'
        }
        sx={{
          flexGrow: 1,
          marginLeft: `${MAIN_MENU_WIDTH}px`,
          background:
            location.pathname === '/dashboard'
              ? theme.colors.pageBackground
              : location.pathname.includes('/security-stack-map')
              ? theme.colors.legacyGreyscale10
              : location.pathname === '/asset-coverage-analyzer'
              ? theme.colors.pageBackground
              : location.pathname.includes('/library/categories')
              ? theme.colors.pageBackground
              : isJiraSetupPath(location.pathname)
              ? theme.colors.pageBackground
              : location.pathname.includes('library/cpis/cpi')
              ? 'transparent'
              : location.pathname === '/settings/account-management'
              ? theme.colors.pageBackground
              : location.pathname === '/settings/user-management'
              ? theme.colors.pageBackground
              : location.pathname === '/settings/profile'
              ? theme.colors.pageBackground
              : location.pathname === '/settings/audit-log'
              ? theme.colors.pageBackground
              : isIntegrationsListPath(location.pathname)
              ? theme.colors.pageBackground
              : isIntegrationSetupPath(location.pathname) && !isJiraSetupPath(location.pathname)
              ? theme.colors.pageBackground
              : location.pathname.includes('/settings')
              ? theme.colors.primaryDark
              : location.pathname.includes('/library')
              ? theme.colors.legacyBackgroundDarker2
              : theme.colors.backgroundDarker,
          bgcolor: location.pathname.includes('/framework-configuration')
            ? theme.colors.analyzerPanelDarker
            : location.pathname === '/insights'
            ? theme.colors.analyzerPanelDarker
            : location.pathname === '/dashboard'
            ? theme.colors.pageBackground
            : location.pathname.includes('user-management')
            ? theme.colors.pageBackground
            : location.pathname.includes('/library') || location.pathname.includes('/security-stack-map')
            ? 'unset'
            : location.pathname === '/asset-coverage-analyzer'
            ? theme.colors.pageBackground
            : location.pathname === '/reports'
            ? theme.colors.analyzerPanelDarker
            : location.pathname === '/settings/account-management'
            ? theme.colors.analyzerPanelDarker
            : location.pathname === '/settings/profile'
            ? theme.colors.analyzerPanelDarker
            : location.pathname === '/settings/audit-log'
            ? theme.colors.analyzerPanelDarker
            : isIntegrationsListPath(location.pathname)
            ? theme.colors.analyzerPanelDarker
            : isIntegrationSetupPath(location.pathname) && !isJiraSetupPath(location.pathname)
            ? theme.colors.analyzerPanelDarker
            : theme.colors.primaryDarker,
          backgroundColor:
            isJiraSetupPath(location.pathname)
              ? theme.colors.pageBackground
              : '',
          maxWidth: `calc(100% - ${MAIN_MENU_WIDTH}px)`,
          height:
            location.pathname === '/dashboard'
              ? 'auto'
              : isIntegrationSetupPath(location.pathname)
              ? '100vh'
              : location.pathname.includes('/settings') || location.pathname.includes('/framework-configuration/')
              ? '100vh'
              : location.pathname.includes('/library/categories')
              ? '100%'
              : location.pathname === '/insights' || location.pathname === '/reports'
              ? '100vh'
              : 'auto',
          minHeight: location.pathname.includes('/library/categories')
            ? undefined
            : location.pathname.includes('/library') ||
              location.pathname.includes('user-management') ||
              location.pathname.includes('/settings/audit-log') ||
              location.pathname.includes('/settings/account-management') ||
              location.pathname.includes('/framework-configuration') ||
              location.pathname === '/asset-coverage-analyzer' ||
              location.pathname === '/insights' ||
              location.pathname === '/nexa-ai' ||
              location.pathname.includes('/reports')
            ? '100vh'
            : location.pathname === '/dashboard'
            ? 'calc(100vh - 8px)'
            : undefined,
          overflow: location.pathname === '/insights' || location.pathname === '/reports' ? 'hidden' : undefined,
          borderRadius:
            location.pathname === '/dashboard'
              ? theme.attributes.borderRadius.secondary
              : location.pathname.includes('/library')
              ? `${theme.attributes.borderRadius.quaternary} 0px 0px ${theme.attributes.borderRadius.quaternary}`
              : 'unset',
          padding:
            location.pathname === '/dashboard'
              ? `0px 0 ${theme.spacing.xSmall}px 0`
              : location.pathname.includes('/library') ||
                location.pathname === '/settings/account-management' ||
                location.pathname.includes('/security-stack-map')
              ? '0'
              : location.pathname === '/asset-coverage-analyzer' ||
                location.pathname === '/insights' ||
                location.pathname === '/nexa-ai' ||
                location.pathname.includes('/reports') ||
                location.pathname === '/framework-configuration'
              ? '0'
              : `0px 0px ${theme.spacing.medium}px 0px`,
          justifyContent: location.pathname === '/dashboard' ? 'center' : 'flex-start',
          display: location.pathname === '/dashboard' ? 'flex' : 'block',
          // Design review: these two pages sit on the plain page background in light.
          "html[data-theme='light'] &": {
            bgcolor:
              location.pathname.includes('/framework-configuration') || location.pathname === '/insights'
                ? theme.colors.pageBackground
                : undefined
          }
        }}
      >
        {showAppBar && <Toolbar />}
        {/* Keyed here and no higher: the sidebar owns the switching overlay and has to stay alive. */}
        <Routes key={accountEpoch}>
          <Route
            path="/library"
            element={
              <ProtectedRoute user={authUser} permission={Permission.CPILibraryView}>
                <Library />
              </ProtectedRoute>
            }
          />
          <Route
            path="/asset-coverage-analyzer"
            element={
              <ProtectedRoute user={authUser} permission={'all'}>
                <CoverageAnalyzer />
              </ProtectedRoute>
            }
          />
          {isAssetManagementEnabled && (
            <Route
              path="/asset-inventory"
              element={
                <ProtectedRoute user={authUser} permission={'all'}>
                  <AssetInventory />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/insights"
            element={
              <ProtectedRoute user={authUser} permission={'all'}>
                <OnyxAIInsights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/nexa-ai"
            element={
              nexaAIEnabled || nexaAIActivating ? (
                <ProtectedRoute user={authUser} roles={[Role.Owner, Role.Admin]}>
                  <NexaAI />
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/reports-old"
            element={
              <ProtectedRoute user={authUser} permission={'all'}>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports-old/customize-cpi-report"
            element={
              <ProtectedRoute user={authUser} permission={'all'}>
                <CustomizeReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports-old/customize-quarterly-report"
            element={
              <ProtectedRoute user={authUser} permission={'all'}>
                <QuarterlyReportBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports-old/customize-monthly-report"
            element={
              <ProtectedRoute user={authUser} permission={'all'}>
                <MonthlyReportBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports-old/customize-full-report"
            element={
              <ProtectedRoute user={authUser} permission={'all'}>
                <FullReportBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/library/cpis/:cpiName/:tabValue"
            element={
              <ProtectedRoute user={authUser} permission={Permission.CPIConfigView}>
                <CPIDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/library/categories/:categoryName"
            element={
              <ProtectedRoute user={authUser} permission={Permission.CPIConfigView}>
                <CategoryPage />
              </ProtectedRoute>
            }
          />
          <Route path="/settings" element={<Settings />} />
          <Route
            path={DATA_SOURCES_PATH}
            element={
              <ProtectedRoute
                user={authUser}
                permission={Permission.ConfigIntegrationsList}
                deniedRoles={[Role.Viewer]}
              >
                <Integrations />
              </ProtectedRoute>
            }
          />
          {/* The addresses both sections used to answer on. The callback route below is matched
              first, so the provider still lands where its redirect URI points. */}
          <Route path={LEGACY_INTEGRATIONS_PATH} element={<LegacyIntegrationsRedirect />} />
          <Route path={`${LEGACY_INTEGRATIONS_PATH}/*`} element={<LegacyIntegrationsRedirect />} />
          <Route
            path="/reports"
            element={
              <ProtectedRoute user={authUser} permission={'all'}>
                <ReportsV2 />
              </ProtectedRoute>
            }
          />

          {/* Jira add wizard; the instance-scoped route resumes an in-progress onboarding. */}
          <Route
            path={`${DATA_SOURCES_PATH}/atlassian_jira/add`}
            element={
              <ProtectedRoute user={authUser} permission={Permission.ConfigIntegrationsConfigView}>
                <SetupJiraInstance />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${DATA_SOURCES_PATH}/atlassian_jira/:instanceId/add`}
            element={
              <ProtectedRoute user={authUser} permission={Permission.ConfigIntegrationsConfigView}>
                <SetupJiraInstance />
              </ProtectedRoute>
            }
          />
          {/* Editing an instance opens the same wizard, so an unfinished onboarding can be completed. */}
          <Route
            path={`${DATA_SOURCES_PATH}/atlassian_jira/:instanceId/edit`}
            element={
              <ProtectedRoute user={authUser} permission={Permission.ConfigIntegrationsConfigView}>
                <SetupJiraInstance />
              </ProtectedRoute>
            }
          />

          <Route
            path={TICKETING_SYSTEMS_PATH}
            element={
              <ProtectedRoute
                user={authUser}
                permission={Permission.ConfigIntegrationsList}
                deniedRoles={[Role.Viewer]}
              >
                <ApplicationsCatalog />
              </ProtectedRoute>
            }
          />
          {/* Where the provider sends the browser back; it completes the approval and opens the setup
              page below. */}
          <Route
            path={APPLICATION_CALLBACK_PATH}
            element={
              <ProtectedRoute user={authUser} permission={Permission.ConfigIntegrationsConfigView}>
                <ApplicationCallback />
              </ProtectedRoute>
            }
          />
          {/* Application setup for outbound integrations; the instance-scoped route is the one a
              reload resumes from. */}
          <Route
            path={`${TICKETING_SYSTEMS_PATH}/:slug/add`}
            element={
              <ProtectedRoute user={authUser} permission={Permission.ConfigIntegrationsConfigView}>
                <SetupApplicationInstance />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${TICKETING_SYSTEMS_PATH}/:slug/:applicationId/add`}
            element={
              <ProtectedRoute user={authUser} permission={Permission.ConfigIntegrationsConfigView}>
                <SetupApplicationInstance />
              </ProtectedRoute>
            }
          />

          <Route
            path={`${DATA_SOURCES_PATH}/:integrationName/:instanceId/edit`}
            element={
              <ProtectedRoute user={authUser} permission={Permission.ConfigIntegrationsConfigView}>
                <SetupIntegrationInstance setupType="edit" />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${DATA_SOURCES_PATH}/:integrationName/add`}
            element={
              <ProtectedRoute user={authUser} permission={Permission.ConfigIntegrationsConfigView}>
                <SetupIntegrationInstance setupType="add" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/user-management"
            element={
              <ProtectedRoute user={authUser} permission={Permission.UsersProfileView}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/profile"
            element={
              <ProtectedRoute user={authUser} permission={'all'}>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/account-management"
            element={
              <ProtectedRoute user={authUser} permission={Permission.AccountManagementView}>
                <AccountManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/security-stack-map"
            element={
              <ProtectedRoute user={authUser} permission="all">
                <SSM />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/audit-log"
            element={
              <ProtectedRoute user={authUser} permission={Permission.AuditView}>
                <AuditLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/framework-configuration"
            element={
              <ProtectedRoute user={authUser} permission="all">
                <Framework />
              </ProtectedRoute>
            }
          />
          <Route
            path="/framework-configuration/:frameworkName"
            element={
              <ProtectedRoute user={authUser} permission="all">
                <FrameworkConfiguration />
              </ProtectedRoute>
            }
          />
          <Route
            path="/framework-configuration/custom/create"
            element={
              <ProtectedRoute user={authUser} permission="all">
                <ChooseCustomFramework />
              </ProtectedRoute>
            }
          />
          <Route path="/zendesk/sso" element={<ZendeskSso />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </MainLayoutWrapper>
    </MainLayoutContainer>
  )
}

export default MainLayout
